import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Dialog,
    DialogContent,
    Autocomplete,
    TextField,
    Button,
    Chip,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Close, Inventory2 } from '@mui/icons-material';
import { motion, useReducedMotion } from 'framer-motion';
import { useQuery, useMutation } from '@tanstack/react-query';
import { WarehouseAPI } from '../../services/api';
import { PackagingCatalogItem, PackagingPerSkuDiff } from '../../types';
import { ImageThumb } from './CardingImageComponents';
import { tokens } from '../../theme/tokens';

interface EditPackagingDialogProps {
    open: boolean;
    skuCode: string;
    onClose: () => void;
    onSaved: () => void;
}

// Per-SKU packaging editor: a catalog-backed multiselect pre-filled with the SKU's current
// resolved packaging, saving a manual override and surfacing the backend's added/removed
// diff. Matches the CardingTab Move-dialog visual language (rounded Paper, orange gradient
// header, framer-motion entrance gated by reduced-motion).
const EditPackagingDialog: React.FC<EditPackagingDialogProps> = ({ open, skuCode, onClose, onSaved }) => {
    const reduceMotion = useReducedMotion();

    const [selected, setSelected] = useState<PackagingCatalogItem[]>([]);
    const [diff, setDiff] = useState<PackagingPerSkuDiff | null>(null);
    const [error, setError] = useState<string | null>(null);

    const catalogQuery = useQuery({
        queryKey: ['packaging-catalog'],
        queryFn: () => WarehouseAPI.getPackagingCatalog(),
        staleTime: 5 * 60 * 1000,
    });

    const mappingQuery = useQuery({
        queryKey: ['sku-mapping', skuCode],
        queryFn: () => WarehouseAPI.getSkuPackagingMapping(skuCode),
        enabled: open && !!skuCode,
    });

    const catalog = useMemo(() => catalogQuery.data || [], [catalogQuery.data]);

    // Pre-fill the selection from the current resolved mapping once both the catalog and the
    // SKU's mapping have loaded. A packaging SKU that resolves but is missing from the catalog
    // is still shown as a chip so the user sees the full current set.
    useEffect(() => {
        if (!open) return;
        const current = mappingQuery.data?.packagingSkus;
        if (!current) return;
        const bySku = new Map(catalog.map(c => [c.sku, c]));
        setSelected(current.map(s => bySku.get(s) || { sku: s, name: null, image: null }));
    }, [open, mappingQuery.data, catalog]);

    // Reset transient state when the dialog closes or switches SKU.
    useEffect(() => {
        if (!open) {
            setDiff(null);
            setError(null);
            setSelected([]);
        }
    }, [open]);

    const saveMutation = useMutation({
        mutationFn: () => WarehouseAPI.putSkuPackagingMapping(skuCode, selected.map(s => s.sku)),
        onSuccess: (result) => {
            setDiff(result);
            setError(null);
            onSaved();
        },
        onError: (err: any) => {
            const rejected = err?.response?.data?.rejected;
            if (Array.isArray(rejected) && rejected.length > 0) {
                setError(`Rejected: ${rejected.map((r: any) => `${r.sku} (${r.reason})`).join('; ')}`);
            } else {
                setError(err?.response?.data?.message || err?.message || 'Failed to update packaging');
            }
        },
    });

    const loading = catalogQuery.isLoading || mappingQuery.isLoading;
    const source = mappingQuery.data?.source;

    const motionProps = reduceMotion
        ? { initial: false as const }
        : {
              initial: { opacity: 0, y: 14, scale: 0.97 },
              animate: { opacity: 1, y: 0, scale: 1 },
              transition: { type: 'spring' as const, stiffness: 340, damping: 28 },
          };

    return (
        <Dialog
            open={open}
            onClose={() => (saveMutation.isPending ? undefined : onClose())}
            maxWidth="sm"
            fullWidth
            slotProps={{ transition: { timeout: 260 } }}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    boxShadow: '0 24px 60px -12px rgba(220, 88, 41,0.25), 0 8px 24px -8px rgba(26, 20, 15,0.15)',
                },
            }}
        >
            <motion.div {...motionProps}>
                {/* Header */}
                <Box sx={{
                    px: 3, pt: 2.5, pb: 2,
                    background: 'linear-gradient(135deg, rgba(220, 88, 41, 0.06) 0%, #ffffff 65%)',
                    borderBottom: '1px solid rgba(220, 88, 41, 0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38, height: 38, borderRadius: '12px', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: `linear-gradient(135deg, #E37E5A 0%, ${tokens.orangeDark} 100%)`,
                            boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                        }}>
                            <Inventory2 sx={{ fontSize: 19, color: '#fff' }} />
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25 }}>
                                Edit packaging
                            </Typography>
                            <Typography variant="caption" sx={{ color: tokens.muted, fontWeight: 500, fontFamily: '"JetBrains Mono", monospace' }}>
                                {skuCode}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={onClose}
                        disabled={saveMutation.isPending}
                        sx={{ color: '#000000', '&:hover': { bgcolor: 'rgba(220, 88, 41, 0.08)', color: tokens.orangeDark } }}
                    >
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: tokens.orangeDark }} />
                        </Box>
                    ) : (
                        <>
                            <Typography variant="overline" sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#a3a3c2', letterSpacing: '0.06em', display: 'block', mb: 1 }}>
                                Packaging SKUs
                                {source && (
                                    <Chip
                                        label={source === 'manual' ? 'manual override' : source === 'plytix' ? 'from Plytix' : 'none set'}
                                        size="small"
                                        sx={{ ml: 1, height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: '#FAFAF7', color: tokens.muted }}
                                    />
                                )}
                            </Typography>

                            <Autocomplete
                                multiple
                                size="small"
                                options={catalog}
                                value={selected}
                                onChange={(_, val) => { setSelected(val); setDiff(null); }}
                                getOptionLabel={(opt) => opt.sku}
                                isOptionEqualToValue={(opt, val) => opt.sku === val.sku}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.sku} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <ImageThumb images={option.image ? [option.image] : []} label={option.sku} size={32} onClick={() => {}} />
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 600, fontSize: '0.8rem' }}>
                                                {option.sku}
                                            </Typography>
                                            {option.name && (
                                                <Typography variant="caption" noWrap sx={{ color: tokens.muted, display: 'block' }}>
                                                    {option.name}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                )}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={option.sku}
                                            label={option.sku}
                                            size="small"
                                            sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.7rem', fontWeight: 600, bgcolor: '#fef3c7' }}
                                        />
                                    ))
                                }
                                sx={{
                                    mb: 2.5,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: tokens.orange, borderWidth: '1.5px' },
                                    },
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Search packaging SKUs…" />
                                )}
                            />

                            {error && (
                                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '10px' }}>{error}</Alert>
                            )}

                            {diff && (
                                <Box sx={{ mb: 2.5, p: 2, borderRadius: '14px', border: '1px solid #ECECE4', bgcolor: '#fafafa' }}>
                                    <Typography sx={{ fontWeight: 700, color: tokens.ink, fontSize: '0.72rem', letterSpacing: '0.04em', mb: 1 }}>
                                        PACKAGING UPDATED
                                    </Typography>
                                    {diff.added.length === 0 && diff.removed.length === 0 ? (
                                        <Typography variant="caption" sx={{ color: tokens.muted }}>
                                            No changes — the mapping already matched your selection.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {diff.added.map(s => (
                                                <Chip key={`a-${s}`} label={`+ ${s}`} size="small"
                                                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', fontWeight: 700, bgcolor: '#dcfce7', color: '#15803d' }} />
                                            ))}
                                            {diff.removed.map(s => (
                                                <Chip key={`r-${s}`} label={`− ${s}`} size="small"
                                                    sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
                                            ))}
                                        </Box>
                                    )}
                                    <Typography variant="caption" sx={{ color: tokens.muted, display: 'block', mt: 1 }}>
                                        {diff.added.length} added · {diff.removed.length} removed · {diff.unchanged.length} unchanged
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', pt: 0.5 }}>
                                <Button
                                    onClick={onClose}
                                    disabled={saveMutation.isPending}
                                    sx={{ textTransform: 'none', fontWeight: 600, color: tokens.muted, borderRadius: '10px', px: 2 }}
                                >
                                    {diff ? 'Close' : 'Cancel'}
                                </Button>
                                <motion.div whileTap={reduceMotion ? undefined : { scale: 0.96 }} style={{ display: 'inline-block' }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => saveMutation.mutate()}
                                        disabled={saveMutation.isPending || loading}
                                        sx={{
                                            textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 2.5, py: 1,
                                            background: `linear-gradient(135deg, #E37E5A 0%, ${tokens.orangeDark} 100%)`,
                                            boxShadow: '0 4px 14px rgba(220, 88, 41,0.35)',
                                            '&:hover': { boxShadow: '0 6px 18px rgba(220, 88, 41,0.45)' },
                                            '&.Mui-disabled': { background: '#e5e7eb', boxShadow: 'none' },
                                        }}
                                    >
                                        {saveMutation.isPending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Save packaging'}
                                    </Button>
                                </motion.div>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </motion.div>
        </Dialog>
    );
};

export default EditPackagingDialog;
